import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    XCircle,
    CheckCircle,
    Clock,
    Mail,
    User,
    FileText,
    Paperclip,
    Send,
    ExternalLink,
    Calendar,
    Eye,
    Copy,
    AlertCircle,
    Loader2
} from 'lucide-react';
import { apiFetch } from '../api';
import { ensureSocketConnection } from '../websocket/socketUtils';
import { useWebSocket } from '../websocket/WebSocketContext';
import AttachmentViewerModal from './AttachmentViewer';

// ── Portal wrapper — renders children directly under document.body,
//    completely escaping any parent overflow/transform constraints. ──────────
function Portal({ children }) {
    return ReactDOM.createPortal(children, document.body);
}

export default function EmailDetails({ email, onClose, setError, loginId, deliveryMode }) {
    const { client } = useWebSocket();
    const [isBodyLoading, setIsBodyLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [showHtmlPreview, setShowHtmlPreview] = useState(false);
    const [emailBody, setEmailBody] = useState('');
    const [emailBodyData, setEmailBodyData] = useState('');

    // ── Lock body scroll while modal is open ────────────────────────────────
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    // ── Close on Escape ─────────────────────────────────────────────────────
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    // ── Fetch email body ────────────────────────────────────────────────────
    const safeJson = async (res) => { try { return await res.json(); } catch { return null; } };

    const extractBody = (data) => {
        if (!data) return null;
        if (data[' DATA ']?.body) return data[' DATA '].body;
        if (data.DATA?.body) return data.DATA.body;
        if (data.data?.body) return data.data.body;
        if (data.Response?.body) return data.Response.body;
        if (typeof data.body === 'string') return data.body;
        if (data.STATUS === 'SUCCESS' && Array.isArray(data.Response))
            return data.Response[0]?.body ?? null;
        return null;
    };

    const fetchEmailBodyById = async (emailId) => {
        if (!emailId) { setError('Missing email id'); return; }
        setIsBodyLoading(true);
        setError('');
        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); }
                catch { setError('Socket connection failed.'); setIsBodyLoading(false); return; }

                const topic = `/topic/get/email/body/${loginId}`;
                const sub = client.subscribe(topic, (msg) => {
                    try {
                        const raw = JSON.parse(msg.body);
                        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        setEmailBodyData(data?.[' DATA '] ?? '');
                        const body = extractBody(data);
                        body ? setEmailBody(body) : setError('Email body not found.');
                    } catch { setError('Failed to parse email body data.'); }
                    finally { sub.unsubscribe(); setIsBodyLoading(false); }
                });
                client.publish({ destination: '/app/get/email/body', body: emailId });
            } else {
                const res = await apiFetch(`/get/email/body?emailId=${encodeURIComponent(emailId)}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await safeJson(res);
                setEmailBodyData(data?.[' DATA '] ?? '');
                const body = extractBody(data);
                body ? setEmailBody(body) : setError('Email body not found.');
                setIsBodyLoading(false);
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            setError('Failed to load email body.');
            setEmailBody('');
            setIsBodyLoading(false);
        }
    };

    useEffect(() => { fetchEmailBodyById(email._id); }, [email]);

    // ── Helpers ─────────────────────────────────────────────────────────────
    const statusColor = (s) => ({ SUCCESS: 'text-green-400', FAILED: 'text-red-400' }[s] ?? 'text-yellow-400');
    const statusBg = (s) => ({ SUCCESS: 'bg-green-500/20 border-green-500/30', FAILED: 'bg-red-500/20 border-red-500/30' }[s] ?? 'bg-yellow-500/20 border-yellow-500/30');
    const StatusIcon = (s) => ({ SUCCESS: CheckCircle, FAILED: XCircle }[s] ?? Clock);

    const fmtDate = (d) => {
        if (!d) return 'N/A';
        try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
        catch { return d; }
    };

    const parseEmails = (s) => s ? s.split(',').map(e => e.trim()).filter(Boolean) : [];

    const escapeHtml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    const handleResend = async () => {
        if (email.STATUS !== 'FAILED') { setError('Only failed emails can be resent'); return; }
        setIsResending(true);
        try {
            const payload = {
                to: email.to, subject: email.subject, body: emailBody, appName: email.appName,
                body_type: email?.body_type ? 'Y' : 'N', cc: email.cc ?? null, bcc: email.bcc ?? null,
                email_attachment: email.email_attachment ?? false,
                attachment_format: email.attachment_format ?? null,
                attachment_data: emailBodyData?.attachment_data ?? email.attachment_data ?? null,
                attachment_data_1: emailBodyData?.attachment_data_1 ?? email.attachment_data_1 ?? null,
                attachment_data_2: emailBodyData?.attachment_data_2 ?? email.attachment_data_2 ?? null,
                email_attachment_compress: email.email_attachment_compress ?? false,
            };
            const res = await apiFetch('/email/send', { method: 'POST', body: JSON.stringify(payload) });
            if (!res.ok) throw new Error(`${res.status}`);
            const data = await res.json();
            if (data.STATUS === 'SUCCESS') { alert('Email resent successfully!'); onClose(); }
            else throw new Error(data.message || 'Failed');
        } catch (e) { setError('Failed to resend: ' + e.message); }
        finally { setIsResending(false); }
    };

    const handleViewRaw = () => {
        const safe = { ...email, body: emailBody ? escapeHtml(emailBody) : emailBody };
        const w = window.open('', '_blank');
        w.document.write(`<html><head><title>Raw Email</title><style>body{background:#1e293b;color:#e2e8f0;font-family:monospace;padding:20px;margin:0}pre{background:#0f172a;padding:20px;border-radius:8px;overflow-x:auto;border:1px solid #334155;white-space:pre-wrap}h1{color:#10b981}</style></head><body><h1>Raw Email Data</h1><pre>${JSON.stringify(safe, null, 2)}</pre></body></html>`);
        w.document.close();
    };

    const handleCopyHtml = async () => {
        const txt = escapeHtml(emailBody || '');
        try { await (navigator?.clipboard?.writeText?.(txt) ?? Promise.reject()); }
        catch { const t = document.createElement('textarea'); t.value = txt; t.style.cssText = 'position:fixed;opacity:0'; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); }
        alert('HTML copied to clipboard!');
    };

    // ── Reusable label ───────────────────────────────────────────────────────
    const Label = ({ children }) => (
        <p className="text-white/40 text-[10px] uppercase tracking-widest mb-2 font-semibold">{children}</p>
    );

    // ── Chip row for email addresses ─────────────────────────────────────────
    const EmailChips = ({ list, color }) => {
        const map = { green: 'bg-green-500/15 border-green-500/25 text-green-200', blue: 'bg-blue-500/15 border-blue-500/25 text-blue-200', purple: 'bg-purple-500/15 border-purple-500/25 text-purple-200' };
        const iconMap = { green: 'text-green-400', blue: 'text-blue-400', purple: 'text-purple-400' };
        return (
            <div className="flex flex-wrap gap-1.5">
                {list.map((addr, i) => (
                    <span key={i} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-mono ${map[color]}`}>
                        <Mail className={`w-3 h-3 flex-shrink-0 ${iconMap[color]}`} />{addr}
                    </span>
                ))}
            </div>
        );
    };

    if (!email) return null;
    const Icon = StatusIcon(email.STATUS);

    // ── Attachment table helper ──────────────────────────────────────────────
    const AttachBlock = ({ title, data }) => {
        const [viewerOpen, setViewerOpen] = useState(false);
        if (!data?.length) return null;

        return (
            <div>
                <div className="flex items-center justify-between mb-2">
                    <p className="text-white/60 text-xs font-medium">{title} ({data.length} records)</p>
                    <button
                        onClick={() => setViewerOpen(true)}
                        className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/15 border border-blue-500/25 text-blue-200 rounded-lg hover:bg-blue-500/25 transition-all text-xs font-medium"
                    >
                        <Eye className="w-3 h-3" /> View Full
                    </button>
                </div>

                {/* Existing compact table */}
                <div className="max-h-36 overflow-y-auto space-y-1.5">
                    {data.map((d, i) => (
                        <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-2.5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                            {Object.entries(d ?? {}).map(([k, v]) => (
                                <div key={k} className="flex gap-1">
                                    <span className="text-white/40 capitalize flex-shrink-0">{k}:</span>
                                    <span className="text-white/90 truncate">
                                        {v === null || v === undefined
                                            ? 'N/A'
                                            : typeof v === 'object'
                                                ? JSON.stringify(v)
                                                : String(v)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                {viewerOpen && (
                    <AttachmentViewerModal
                        title={title}
                        data={data}
                        onClose={() => setViewerOpen(false)}
                    />
                )}
            </div>
        );
    };

    // ════════════════════════════════════════════════════════════════════════
    //  RENDER — everything goes through <Portal> so it mounts on document.body
    // ════════════════════════════════════════════════════════════════════════
    return (
        <Portal>
            {/* ── Main overlay ── */}
            <div
                className="fixed inset-0 flex items-center justify-center"
                style={{ zIndex: 99999 }}
            >
                {/* Backdrop */}
                <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />

                {/* ── Modal card ──
                    w-[calc(100vw-2rem)] ensures it never exceeds the visible viewport
                    regardless of sidebar width. max-w-3xl caps it on large screens.    */}
                <div
                    className="relative flex flex-col bg-slate-900 border border-white/15 rounded-2xl shadow-2xl overflow-hidden"
                    style={{
                        width: 'min(calc(100vw - 2rem), 780px)',
                        maxHeight: 'calc(100vh - 2rem)',
                        zIndex: 100000,
                    }}
                >
                    {/* ── Header ── */}
                    <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-6 py-4 border-b border-white/10 bg-white/5">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                                <Mail className="w-4 h-4 text-white" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="text-base sm:text-lg font-bold text-white">Email Details</h3>
                                <p className="text-[10px] text-white/35 font-mono truncate hidden sm:block">{email._id}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="flex-shrink-0 ml-3 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                            <XCircle className="w-4 h-4 text-white/70" />
                        </button>
                    </div>

                    {/* ── Scrollable body ── */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
                        <div className="px-5 sm:px-6 py-5 space-y-5">

                            {/* Status + ID */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Status</Label>
                                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${statusBg(email.STATUS)}`}>
                                        <Icon className={`w-4 h-4 ${statusColor(email.STATUS)}`} />
                                        <span className={`text-sm font-semibold ${statusColor(email.STATUS)}`}>{email.STATUS}</span>
                                    </div>
                                </div>
                                <div>
                                    <Label>Email ID</Label>
                                    <p className="text-white/80 text-xs font-mono bg-white/5 border border-white/10 px-3 py-2 rounded-lg break-all leading-relaxed">{email._id}</p>
                                </div>
                            </div>

                            {/* Reference fields (conditional) */}
                            {(email?.rf1_id || email?.rf1_name || email?.rf2_id || email?.rf2_name) && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    {[['Site ID', email.rf1_id], ['Site Name', email.rf1_name], ['Location ID', email.rf2_id], ['Location Name', email.rf2_name]].filter(([, v]) => v).map(([lbl, val]) => (
                                        <div key={lbl}>
                                            <Label>{lbl}</Label>
                                            <p className="text-white/80 text-xs font-mono break-all">{val}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Subject */}
                            <div>
                                <Label>Subject</Label>
                                <p className="text-white text-sm sm:text-base font-semibold bg-white/5 border border-white/10 px-4 py-3 rounded-xl">{email.subject || 'No Subject'}</p>
                            </div>

                            {/* App name + Created */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Application Name</Label>
                                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-2.5 rounded-xl">
                                        <User className="w-4 h-4 text-green-400 flex-shrink-0" />
                                        <span className="text-white text-sm font-medium truncate">{email.appName || 'N/A'}</span>
                                    </div>
                                </div>
                                <div>
                                    <Label>Created</Label>
                                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-2.5 rounded-xl">
                                        <Clock className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                        <span className="text-white text-sm">{fmtDate(email.creation_time_str)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-white/10" />

                            {/* Recipients */}
                            <div className="space-y-3">
                                <p className="text-xs font-semibold text-white/50 uppercase tracking-widest">Recipients</p>
                                {[['To', parseEmails(email.to), 'green'], ['CC', parseEmails(email.cc), 'blue'], ['BCC', parseEmails(email.bcc), 'purple']]
                                    .filter(([, list]) => list.length > 0)
                                    .map(([label, list, color]) => (
                                        <div key={label}>
                                            <Label>{label}</Label>
                                            <div className="bg-white/5 border border-white/10 rounded-xl px-3 py-3">
                                                <EmailChips list={list} color={color} />
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>

                            <div className="border-t border-white/10" />

                            {/* Message Body */}
                            <div>
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                    <Label>Message Body</Label>
                                    {email.body_type && emailBody && (
                                        <div className="flex items-center gap-2 mb-2">
                                            <button onClick={handleCopyHtml} className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500/15 border border-green-500/25 text-green-200 rounded-lg hover:bg-green-500/25 transition-all text-xs font-medium">
                                                <Copy className="w-3 h-3" />Copy HTML
                                            </button>
                                            <button onClick={() => setShowHtmlPreview(true)} className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/15 border border-blue-500/25 text-blue-200 rounded-lg hover:bg-blue-500/25 transition-all text-xs font-medium">
                                                <Eye className="w-3 h-3" />Preview HTML
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                                    {isBodyLoading ? (
                                        <div className="flex items-center justify-center gap-3 py-10">
                                            <Loader2 className="w-5 h-5 animate-spin text-green-400" />
                                            <span className="text-white/50 text-sm">Loading email body…</span>
                                        </div>
                                    ) : email.body_type && emailBody ? (
                                        <div className="p-4 text-sm text-white leading-relaxed max-h-56 overflow-y-auto" dangerouslySetInnerHTML={{ __html: emailBody }} />
                                    ) : (
                                        <div className="flex items-start gap-3 p-4">
                                            <FileText className="w-4 h-4 text-white/40 flex-shrink-0 mt-0.5" />
                                            <p className="text-white/80 text-sm whitespace-pre-wrap max-h-56 overflow-y-auto">{emailBody || 'No message body provided'}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Attachments */}
                            {(email.email_attachment || emailBodyData?.attachment_data?.length > 0 || email.attachment_data?.length > 0) && (
                                <div>
                                    <Label>Attachments</Label>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                                        {email.attachment_format && (
                                            <div className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <Paperclip className="w-4 h-4 text-blue-400" />
                                                    <div>
                                                        <p className="text-blue-200 text-sm font-medium">Format</p>
                                                        <p className="text-blue-300 text-xs uppercase">{email.attachment_format}</p>
                                                    </div>
                                                </div>
                                                {email.email_attachment_compress && <span className="text-xs bg-blue-500/20 text-blue-200 px-2 py-0.5 rounded-full">Compressed</span>}
                                            </div>
                                        )}
                                        <AttachBlock title="Attachment Data" data={emailBodyData?.attachment_data?.length > 0 ? emailBodyData.attachment_data : email.attachment_data} />
                                        <AttachBlock title="Attachment Data One" data={emailBodyData?.attachment_data_1?.length > 0 ? emailBodyData.attachment_data_1 : email.attachment_data_1} />
                                        <AttachBlock title="Attachment Data Two" data={emailBodyData?.attachment_data_2?.length > 0 ? emailBodyData.attachment_data_2 : email.attachment_data_2} />
                                    </div>
                                </div>
                            )}

                            {/* Server response */}
                            {email.email_response && (
                                <div>
                                    <Label>Server Response</Label>
                                    <div className={`border rounded-xl p-4 flex items-start gap-3 ${email.STATUS === 'FAILED' ? 'bg-red-500/10 border-red-500/20' : 'bg-green-500/10 border-green-500/20'}`}>
                                        {email.STATUS === 'FAILED' ? <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" /> : <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />}
                                        <p className={`text-sm ${email.STATUS === 'FAILED' ? 'text-red-200' : 'text-green-200'}`}>{email.email_response}</p>
                                    </div>
                                </div>
                            )}

                            {/* Timestamps */}
                            <div>
                                <Label>Timestamp Information</Label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {[['Date', Calendar, 'text-blue-400', email.date], ['Creation Time', Clock, 'text-green-400', email.creation_time_str], ['Time (ms)', Clock, 'text-purple-400', email.creation_time_ms]].map(([lbl, Ic, cls, val]) => (
                                        <div key={lbl} className="bg-white/5 border border-white/10 rounded-xl p-3">
                                            <div className="flex items-center gap-1.5 mb-1.5"><Ic className={`w-3.5 h-3.5 ${cls}`} /><p className="text-white/40 text-xs">{lbl}</p></div>
                                            <p className="text-white text-xs font-mono">{val || 'N/A'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Client info */}
                            {(email.client_ip || email.user_agent) && (
                                <div>
                                    <Label>Client Information</Label>
                                    <div className="space-y-2">
                                        {email.client_ip && (
                                            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                                                <div className="flex items-center gap-1.5 mb-1"><AlertCircle className="w-3.5 h-3.5 text-orange-400" /><p className="text-white/40 text-xs">Client IP</p></div>
                                                <p className="text-white text-sm font-mono">{email.client_ip}</p>
                                            </div>
                                        )}
                                        {email.user_agent && (
                                            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                                                <div className="flex items-center gap-1.5 mb-1"><AlertCircle className="w-3.5 h-3.5 text-orange-400" /><p className="text-white/40 text-xs">User Agent</p></div>
                                                <p className="text-white text-xs font-mono break-all leading-relaxed">{email.user_agent}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="h-1" />
                        </div>
                    </div>

                    {/* ── Sticky footer ── */}
                    <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-2 px-5 sm:px-6 py-4 border-t border-white/10 bg-slate-900/80">
                        <button onClick={onClose} className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm font-medium transition-colors">
                            Close
                        </button>
                        {email.STATUS === 'FAILED' && (
                            <button onClick={handleResend} disabled={isResending} className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 text-green-200 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                {isResending ? <><Loader2 className="w-4 h-4 animate-spin" />Resending…</> : <><Send className="w-4 h-4" />Resend Email</>}
                            </button>
                        )}
                        <button onClick={handleViewRaw} className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-200 rounded-xl text-sm font-medium transition-colors">
                            <ExternalLink className="w-4 h-4" />View Raw Data
                        </button>
                    </div>
                </div>
            </div>

            {/* ── HTML Preview modal (also portalled, layered on top) ── */}
            {showHtmlPreview && (
                <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 100001 }}>
                    <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={() => setShowHtmlPreview(false)} />
                    <div
                        className="relative flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
                        style={{ width: 'min(calc(100vw - 2rem), 900px)', maxHeight: 'calc(100vh - 2rem)', zIndex: 100002 }}
                    >
                        <div className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-base font-bold text-gray-800">HTML Email Preview</h3>
                            <button onClick={() => setShowHtmlPreview(false)} className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center transition-colors">
                                <XCircle className="w-4 h-4 text-gray-700" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto bg-gray-100 p-4">
                            <div className="bg-white rounded-xl shadow-sm">
                                <iframe srcDoc={emailBody} title="Email HTML Preview" className="w-full border-0 rounded-xl" style={{ height: 'calc(100vh - 12rem)' }} sandbox="allow-same-origin allow-scripts" />
                            </div>
                        </div>
                        <div className="flex-shrink-0 flex justify-end px-5 py-3 border-t border-gray-200 bg-gray-50">
                            <button onClick={() => setShowHtmlPreview(false)} className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl text-sm font-medium transition-colors">
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Portal>
    );
}